/**
 * Preset Search API Implementation
 * 
 * Modern search functionality with comprehensive filtering and relevance scoring.
 * Provides unified search capabilities for chord progressions with:
 * - Smart filter detection from natural language queries
 * - Relevance scoring and match explanations
 * - Multiple search modes and sorting options
 * - Secure input validation and performance optimization
 * 
 * @example
 * ```typescript
 * // Natural language search with automatic filter detection
 * const results = await searchProgressions({
 *   query: "easy jazz guitar",
 *   searchMode: "hybrid",
 *   limit: 20
 * });
 * 
 * // Manual filter-based search
 * const results = await searchProgressions({
 *   filters: {
 *     genres: ["jazz", "blues"],
 *     difficulties: ["beginner", "intermediate"]
 *   },
 *   sortBy: "relevance"
 * });
 * ```
 */

import {
  Progression,
  ApiResponse,
  DEFAULT_ROOT_CONFIG,
  Scale,
  GenreCategory,
  DifficultyLevel,
  AccessLevel,
  PremiumFeature,
  ProgressionSource
} from './presetSchema';

import { getAllProgressions } from './presetAPI';

// ================================================================================
// SEARCH INTERFACES & TYPES
// ================================================================================

/**
 * Search options with hybrid text + filter scoring
 * 
 * Supports sophisticated search capabilities including natural language queries,
 * multiple filter types, and different search modes for optimal flexibility.
 * 
 * @interface SearchOptions
 * @property {string} [query] - Natural language search query
 * @property {object} [filters] - Structured filters to apply
 * @property {'relevance' | 'recent' | 'alphabetical' | 'difficulty'} [sortBy] - Result sorting method
 * @property {number} [limit] - Maximum number of results to return
 * @property {'hybrid' | 'filters_only' | 'text_only'} [searchMode] - Search strategy to use
 */
export interface SearchOptions {
  query?: string;
  filters?: {
    genres?: GenreCategory[];
    difficulties?: DifficultyLevel[];
    keys?: string[];  // e.g., ["C Major", "A Minor"]
    guitarCounts?: string[];  // e.g., ["1-2", "3-4", "5+"]
    accessLevels?: AccessLevel[];
    features?: PremiumFeature[];
    sources?: ProgressionSource[];
    favorites?: boolean;  // Filter for favorite progressions only
  };
  sortBy?: 'relevance' | 'recent' | 'alphabetical' | 'difficulty';
  limit?: number;
  searchMode?: 'hybrid' | 'filters_only' | 'text_only';
}

/**
 * Search result with relevance scoring and match information
 * 
 * Extends the base Progression type with additional metadata about
 * search relevance and what criteria were matched.
 * 
 * @interface ScoredProgression
 * @extends Progression
 * @property {number} [_score] - Relevance score from 0-100
 * @property {object} [_matches] - Debug information about what matched the search
 */
export interface ScoredProgression extends Progression {
  _score?: number;  // Relevance score (0-100)
  _matches?: {      // Debug info showing what matched
    filterMatches: string[];
    textMatches: string[];
  };
}

/**
 * Internal search match interface for scoring calculations
 * 
 * @interface SearchMatch
 * @property {Progression} progression - The matched progression
 * @property {number} score - Calculated relevance score
 * @property {string[]} matchReasons - List of reasons why this progression matched
 */
interface SearchMatch {
  progression: Progression;
  score: number;
  matchReasons: string[];
}

/**
 * Quick filter tab options for the UI
 * 
 * @type QuickFilterTab
 */
export type QuickFilterTab = 'all' | 'mine' | 'library' | 'recent';

// ================================================================================
// MODERN SEARCH ARCHITECTURE - DUAL APPLICATION APPROACH
// ================================================================================

/**
 * 🎯 HYBRID SEARCH STRATEGY: Dual Application Approach
 * 
 * Our modern search engine applies both structured filters AND full-text search
 * simultaneously, then combines the results with intelligent relevance scoring.
 * This approach is used by modern search systems like Elasticsearch and Algolia.
 * 
 * Key Benefits:
 * - Natural language queries work perfectly: "easy jazz guitar lessons"
 * - Semantic meaning preserved: "jazz" works as both filter AND text search
 * - Relevance boost: Items matching both filter + text get highest scores  
 * - Graceful fallback: If no filters match, full-text search still works
 * - User intent respected: Manual filters override detected filters
 * 
 * Query Processing Example: "easy jazz guitar"
 * ├── Filter Detection: { difficulty: "beginner", genre: "jazz" }
 * ├── Text Search: "easy jazz guitar" (full query preserved)
 * └── Dual Application: Apply BOTH simultaneously
 * 
 * Scoring Example Results:
 * 🥇 Score 155: Beginner✓ + Jazz✓ + "guitar" in title + "jazz guitar" in description
 * 🥈 Score 125: Beginner✓ + Jazz✓ + "easy" in description  
 * 🥉 Score 90:  Jazz✓ + "jazz guitar" phrase match (missing difficulty filter)
 * 📊 Score 45:  "easy jazz guitar" text match only (no filter matches)
 * 
 * This ensures that results are both relevant and comprehensive.
 */

// ================================================================================
// UTILITY FUNCTIONS
// ================================================================================

/**
 * Create a standardized API response with timestamp
 * 
 * @template T - The type of data being returned
 * @param {boolean} success - Whether the operation was successful
 * @param {T} [data] - The response data (required for success)
 * @param {string} [error] - Error message (required for failure)
 * @returns {ApiResponse<T>} Standardized API response
 */
function createApiResponse<T>(success: true, data: T): ApiResponse<T>;
function createApiResponse<T>(success: false, data: undefined, error: string): ApiResponse<T>;
function createApiResponse<T>(success: boolean, data?: T, error?: string): ApiResponse<T> {
  return {
    success,
    data,
    error,
    timestamp: Date.now()
  } as ApiResponse<T>;
}

/**
 * Extract chord symbols from a progression for searching
 * 
 * Converts Scale enum values to standard chord symbol notation
 * for use in text-based searches and filtering.
 * 
 * @param {Progression} progression - The progression to extract chords from
 * @returns {string[]} Array of chord symbols (e.g., ["C", "Am", "F", "G7"])
 */
export function getProgressionChords(progression: Progression): string[] {
  if (!progression.arrangements || !Array.isArray(progression.arrangements)) {
    return [];
  }
  
  return progression.arrangements.map(guitar => {
    const rootNote = guitar.root?.note || DEFAULT_ROOT_CONFIG.note;
    const quality = guitar.root?.scale || 'MAJOR_TRIAD';
    
    // Convert Scale enum to chord symbol suffix with proper typing
    const qualitySuffix: Record<Scale, string> = {
      'MAJOR_TRIAD': '',
      'MINOR_TRIAD': 'm',
      'DOMINANT_7TH': '7',
      'MAJOR_7TH': 'maj7',
      'MINOR_7TH': 'm7',
      'DIMINISHED_TRIAD': 'dim',
      'DIMINISHED_7TH': 'dim7',
      'AUGMENTED_TRIAD': 'aug',
      'HALF_DIMINISHED_7TH': 'm7b5',
      'MINOR_MAJOR_7TH': 'mMaj7',
      'AUGMENTED_7TH': 'aug7',
      'AUGMENTED_MAJOR_7TH': 'augMaj7',
      // Scale types (fallbacks)
      'MAJOR': '',
      'MINOR': 'm',
      'HARMONIC_MINOR': 'm',
      'MELODIC_MINOR': 'm',
      'DORIAN': 'm',
      'PHRYGIAN': 'm',
      'LYDIAN': '',
      'MIXOLYDIAN': '',
      'LOCRIAN': 'm',
      'PENTATONIC_MAJOR': '',
      'PENTATONIC_MINOR': 'm',
      'BLUES': 'm',
      'CHROMATIC': '',
      'WHOLE_TONE': '',
      'OCTATONIC_HALF_WHOLE': '',
      'OCTATONIC_WHOLE_HALF': ''
    };
    
    return rootNote + (qualitySuffix[quality] || '');
  });
}

// ================================================================================
// FILTER DETECTION & QUERY PROCESSING
// ================================================================================

/**
 * Detect and suggest filters based on search query text
 * 
 * Analyzes natural language queries to automatically apply appropriate filters.
 * This enables users to search with phrases like "easy jazz guitar" and have
 * the system automatically apply difficulty=beginner and genre=jazz filters.
 * 
 * @param {string} query - The search query to analyze
 * @returns {SearchOptions['filters']} Detected filters object
 * 
 * @example
 * ```typescript
 * const filters = detectFiltersFromQuery("easy jazz guitar");
 * // Returns: { 
 * //   difficulties: ["beginner"], 
 * //   genres: ["jazz"] 
 * // }
 * ```
 */
export function detectFiltersFromQuery(query: string): SearchOptions['filters'] {
  if (!query || !query.trim()) return {};
  
  const queryLower = query.toLowerCase().trim();
  const detectedFilters: SearchOptions['filters'] = {};
  
  // Genre detection with comprehensive keyword mapping and prefix support
  const genreKeywords = {
    'jazz': ['jazz', 'jaz'], // Support prefix matching
    'blues': ['blues', 'blue', 'blu'],
    'rock': ['rock', 'roc'],
    'pop': ['pop'],
    'folk': ['folk', 'acoustic', 'campfire', 'fol'],
    'practice': ['practice', 'exercise', 'study', 'prac', 'exe']
  };
  
  for (const [genre, keywords] of Object.entries(genreKeywords)) {
    if (keywords.some(keyword => {
      // Exact match or prefix match (if query is at least 2 characters)
      return queryLower.includes(keyword) || 
             (queryLower.length >= 2 && keyword.startsWith(queryLower));
    })) {
      detectedFilters.genres = detectedFilters.genres || [];
      detectedFilters.genres.push(genre as GenreCategory);
    }
  }
  
  // Difficulty detection with prefix support
  const difficultyKeywords = {
    'beginner': ['beginner', 'easy', 'simple', 'basic', 'beg', 'eas'],
    'intermediate': ['intermediate', 'medium', 'int', 'med'],
    'advanced': ['advanced', 'complex', 'difficult', 'expert', 'adv', 'exp']
  };
  
  for (const [difficulty, keywords] of Object.entries(difficultyKeywords)) {
    if (keywords.some(keyword => {
      return queryLower.includes(keyword) || 
             (queryLower.length >= 2 && keyword.startsWith(queryLower));
    })) {
      detectedFilters.difficulties = detectedFilters.difficulties || [];
      detectedFilters.difficulties.push(difficulty as DifficultyLevel);
    }
  }
  
  // Source detection
  if (queryLower.includes('my') || queryLower.includes('mine') || queryLower.includes('saved')) {
    detectedFilters.sources = ['user'];
  }
  if (queryLower.includes('library') || queryLower.includes('standard')) {
    detectedFilters.sources = ['standard'];
  }
  
  // Key detection (basic pattern matching)
  const keyPatterns = [
    // Major keys
    'c major', 'g major', 'd major', 'a major', 'e major', 'b major', 'f# major',
    'c# major', 'f major', 'bb major', 'eb major', 'ab major',
    // Minor keys  
    'a minor', 'e minor', 'b minor', 'f# minor', 'c# minor', 'g# minor', 'd# minor',
    'a# minor', 'd minor', 'g minor', 'c minor', 'f minor'
  ];
  
  for (const keyPattern of keyPatterns) {
    if (queryLower.includes(keyPattern)) {
      detectedFilters.keys = detectedFilters.keys || [];
      // Capitalize properly (e.g., "c major" -> "C Major")
      const formattedKey = keyPattern.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      detectedFilters.keys.push(formattedKey);
    }
  }
  
  return detectedFilters;
}

// ================================================================================
// FILTERING FUNCTIONS
// ================================================================================
// HYBRID SEARCH ENGINE
// ================================================================================

/**
 * Calculate hybrid relevance score combining filter matches + text search
 * This is the core of our modern search approach - no keyword stripping!
 */
function calculateHybridRelevanceScore(
  progression: Progression,
  originalQuery: string,
  detectedFilters: SearchOptions['filters'],
  explicitFilters: SearchOptions['filters'] = {}
): SearchMatch {
  let score = 0;
  const matchReasons: string[] = [];
  const queryLower = originalQuery.toLowerCase();
  let hasRelevantMatch = false; // Track if we found actual relevance

  // Base score for all progressions
  score += 10;
  matchReasons.push('📊 base score');

  // =================================================================
  // FILTER MATCH SCORING (High Value - 40-60 points each)
  // =================================================================
  
  // Merge detected and explicit filters for scoring
  const allFilters = { ...detectedFilters, ...explicitFilters };

  // Genre filter match (highest value - very specific)
  if (allFilters?.genres?.includes(progression.metadata?.genre as any)) {
    score += 60;
    matchReasons.push(`🎵 genre: ${progression.metadata.genre}`);
    hasRelevantMatch = true;
  }

  // Difficulty filter match (high value - indicates user skill level)
  if (allFilters?.difficulties?.includes(progression.metadata?.difficulty as any)) {
    score += 55;
    matchReasons.push(`📊 difficulty: ${progression.metadata.difficulty}`);
    hasRelevantMatch = true;
  }

  // Source filter match (high value - user vs library preference)
  if (allFilters?.sources?.includes(progression.source as any)) {
    score += 50;
    matchReasons.push(`📁 source: ${progression.source}`);
    hasRelevantMatch = true;
  }

  // Access level filter match - CONSTRAINT ONLY, not a relevance signal
  // This filter is automatically applied for non-premium users and should not contribute to scoring
  // if (allFilters?.accessLevels?.includes(progression.metadata?.accessLevel as any)) {
  //   // Access level is a constraint filter, not a relevance signal - don't score it
  // }

  // Key filter match
  if (allFilters?.keys?.some(key => 
    progression.metadata?.key?.toLowerCase().includes(key.toLowerCase())
  )) {
    score += 40;
    matchReasons.push(`🎹 key match`);
    hasRelevantMatch = true;
  }

  // Guitar count filter match
  if (allFilters?.guitarCounts) {
    const guitarCount = progression.metadata?.guitarCount || progression.arrangements?.length || 0;
    const matchesCount = allFilters.guitarCounts.some(countRange => {
      switch (countRange) {
        case '1-2': return guitarCount >= 1 && guitarCount <= 2;
        case '3-4': return guitarCount >= 3 && guitarCount <= 4;
        case '5+': return guitarCount >= 5;
        default: return false;
      }
    });
    if (matchesCount) {
      score += 40;
      matchReasons.push(`🎸 guitar count: ${guitarCount}`);
      hasRelevantMatch = true;
    }
  }

  // =================================================================
  // ENHANCED TEXT MATCH SCORING (Medium-High Value - 15-50 points)
  // Uses comprehensive matching with prefix support for metadata fields
  // =================================================================

  if (queryLower.trim()) {
    const textMatches = calculateEnhancedTextMatches(progression, queryLower);
    score += textMatches.score;
    matchReasons.push(...textMatches.matchReasons);
    if (textMatches.hasRelevantMatch) {
      hasRelevantMatch = true;
    }

    // Multi-word phrase matching (e.g., "jazz guitar", "blues rock")
    const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
    if (queryWords.length > 1) {
      const progressionText = [
        progression.name,
        progression.metadata.description || '',
        progression.metadata.pattern || '',
        progression.metadata.key || '',
        progression.metadata?.genre || '',
        progression.metadata?.difficulty || '',
        ...(progression.metadata.examples || [])
      ].join(' ').toLowerCase();

      // Check for multi-word phrase matches
      if (progressionText.includes(queryLower)) {
        score += 35;
        matchReasons.push('🔗 phrase match');
        hasRelevantMatch = true;
      } else {
        // Partial multi-word match
        const matchingWords = queryWords.filter(word => progressionText.includes(word));
        if (matchingWords.length >= 2) {
          score += 25;
          matchReasons.push(`🔤 ${matchingWords.length}/${queryWords.length} words match`);
          hasRelevantMatch = true;
        }
      }
    }
  }

  // =================================================================
  // BOOST SCORING (Context & Usage - 5-25 points)
  // Only apply boosts if we found relevant content or it's an empty search
  // =================================================================

  if (hasRelevantMatch || !queryLower.trim()) {
    // User progressions get priority boost (only if relevant)
    if (progression.source === 'user') {
      score += 25;
      matchReasons.push('👤 user progression');
    }

    // Recently modified gets boost (only if relevant)
    const recentThreshold = Date.now() - (30 * 24 * 60 * 60 * 1000); // 30 days
    if ((progression.metadata.modified || 0) > recentThreshold) {
      score += 15;
      matchReasons.push('⏰ recently modified');
    }

    // Frequently used gets boost (only if relevant)
    const usageCount = progression.metadata.usageCount || 0;
    if (usageCount > 5) {
      score += 20;
      matchReasons.push(`⭐ popular (${usageCount} uses)`);
    } else if (usageCount > 2) {
      score += 10;
      matchReasons.push(`📈 used ${usageCount} times`);
    }

    // Quality indicators (only if relevant)
    if (progression.metadata.description && progression.metadata.description.length > 50) {
      score += 5;
      matchReasons.push('📚 detailed description');
    }

    if (progression.metadata.examples && progression.metadata.examples.length > 0) {
      score += 5;
      matchReasons.push('💡 has examples');
    }
  }
  
  // Apply penalty for irrelevant content when we have a specific query
  if (queryLower.trim() && !hasRelevantMatch) {
    // If we have a query but no relevant match, significantly reduce score
    const originalScore = score;
    score = Math.max(score - 50, 0); // Stronger penalty to ensure irrelevant content is filtered out
    matchReasons.push(`❌ no relevant match for "${queryLower}" - penalized (${originalScore} -> ${score})`);
  }

  // Flag potentially irrelevant high-scoring results for debugging
  if (!hasRelevantMatch && queryLower.trim() && score > 30) {
    matchReasons.push(`⚠️ High score with limited relevance (${score}) for query "${queryLower}"`);
  }

  // Add debug info about why this progression might have scored high
  if (queryLower.trim() && score >= 30) {
    const debugInfo = [];
    if (progression.metadata?.favorite) debugInfo.push('favorite');
    if (progression.source === 'user') debugInfo.push('user');
    if (debugInfo.length > 0) {
      matchReasons.push(`🔍 scoring factors: ${debugInfo.join(', ')}`);
    }
  }

  return {
    progression,
    score,
    matchReasons
  };
}

/**
 * Modern hybrid search: Dual application with smart relevance weighting
 * KEY INSIGHT: Apply filters AND keep full query for text search - no keyword stripping!
 * (Internal function used by main search API)
 */
function hybridSearchProgressions(
  allProgressions: Progression[],
  query: string,
  explicitFilters: SearchOptions['filters'] = {}
): SearchMatch[] {
  if (!query.trim() && (!explicitFilters || Object.keys(explicitFilters).length === 0)) {
    // Empty search with no filters - return all with basic scoring
    return allProgressions.map(progression => ({
      progression,
      score: progression.source === 'user' ? 30 : 10,
      matchReasons: progression.source === 'user' ? ['👤 user progression'] : ['📚 library progression']
    }));
  }

  // Handle empty search WITH explicit filters (e.g., access level filtering)
  if (!query.trim() && explicitFilters && Object.keys(explicitFilters).length > 0) {
    // Filter progressions based on explicit filters only
    const filteredProgressions = allProgressions.filter(progression => 
      matchesFilters(progression, explicitFilters)
    );
    
    return filteredProgressions.map(progression => ({
      progression,
      score: progression.source === 'user' ? 30 : 10,
      matchReasons: progression.source === 'user' ? ['👤 user progression'] : ['📚 library progression']
    }));
  }

  // Detect filters from query text (but DON'T strip keywords!)
  const detectedFilters = query ? detectFiltersFromQuery(query) : {};
  
  // Combine detected and explicit filters
  

  
  // STEP 1: Apply constraint filters first (explicit user selections are hard constraints)
  // These are hard constraints that must be met, not relevance signals
  const constraintFilteredProgressions = allProgressions.filter(progression => 
    matchesFilters(progression, explicitFilters) // Only explicit filters as constraints
  );
  

  
  // STEP 2: Score remaining progressions using only detected relevance filters
  // Detected filters from query text are used for relevance scoring, not as constraints
  const scoredMatches = constraintFilteredProgressions.map(progression => 
    calculateHybridRelevanceScore(progression, query, detectedFilters, detectedFilters)
  );

  // Apply intelligent minimum score threshold based on query specificity
  let minScore = 5; // Default for empty search or filter-only
  
  if (query.trim()) {
    // For text queries, require actual relevance - not just base score + user boost
    const hasDetectedFilters = detectedFilters && Object.keys(detectedFilters).length > 0;
    
    if (hasDetectedFilters) {
      // If we detected filters from query, require filter OR text match (not just user boost)
      // Increase threshold to ensure irrelevant content is filtered out
      minScore = 60; // Base (10) + filter match (50+) OR base (10) + text match (25+) + boost (25+)
    } else {
      // Pure text search - require actual text match
      minScore = 40; // Base (10) + text match (30+) to ensure relevance
    }
  }
  
  const filteredMatches = scoredMatches.filter(match => match.score >= minScore);



  // Sort by score (highest first)
  return filteredMatches.sort((a, b) => b.score - a.score);
}

// ================================================================================
// HELPER FUNCTIONS FOR DIFFERENT SEARCH MODES
// ================================================================================

/**
 * Check if a progression matches the given filters
 */
function matchesFilters(progression: Progression, filters: SearchOptions['filters']): boolean {
  if (!filters) return true;
  
  // Check genre filter
  if (filters.genres && filters.genres.length > 0) {
    if (!filters.genres.includes(progression.metadata?.genre as any)) return false;
  }
  
  // Check difficulty filter
  if (filters.difficulties && filters.difficulties.length > 0) {
    if (!filters.difficulties.includes(progression.metadata?.difficulty as any)) return false;
  }
  
  // Check source filter
  if (filters.sources && filters.sources.length > 0) {
    if (!filters.sources.includes(progression.source as any)) return false;
  }
  
  // Check access level filter
  if (filters.accessLevels && filters.accessLevels.length > 0) {
    const accessLevel = progression.metadata?.accessLevel || 'free';
    if (!filters.accessLevels.includes(accessLevel)) return false;
  }
  
  // Check key filter
  if (filters.keys && filters.keys.length > 0) {
    const progressionKey = progression.metadata?.key;
    if (!progressionKey || !filters.keys.some(key => 
      progressionKey.toLowerCase().includes(key.toLowerCase())
    )) return false;
  }
  
  // Check guitar count filter
  if (filters.guitarCounts && filters.guitarCounts.length > 0) {
    const guitarCount = progression.metadata?.guitarCount || progression.arrangements?.length || 0;
    const matchesCount = filters.guitarCounts.some(countRange => {
      switch (countRange) {
        case '1-2': return guitarCount >= 1 && guitarCount <= 2;
        case '3-4': return guitarCount >= 3 && guitarCount <= 4;
        case '5+': return guitarCount >= 5;
        default: return false;
      }
    });
    if (!matchesCount) return false;
  }
  
  // Check favorites filter
  if (filters.favorites !== undefined) {
    const isFavorite = progression.metadata?.favorite === true;
    if (filters.favorites && !isFavorite) return false;
    if (!filters.favorites && isFavorite) return false;
  }
  
  return true;
}

/**
 * Check if a progression matches the text query
 */
function matchesTextQuery(progression: Progression, query: string): boolean {
  if (!query || !query.trim()) return true;
  
  const queryLower = query.toLowerCase().trim();
  
  // Search in name
  if (progression.name.toLowerCase().includes(queryLower)) return true;
  
  // Search in description
  if (progression.metadata.description?.toLowerCase().includes(queryLower)) return true;
  
  // Search in pattern
  if (progression.metadata.pattern?.toLowerCase().includes(queryLower)) return true;
  
  // Search in key
  if (progression.metadata.key?.toLowerCase().includes(queryLower)) return true;
  
  // Search in chord symbols
  const chords = getProgressionChords(progression);
  if (chords.some(chord => chord.toLowerCase().includes(queryLower))) return true;
  
  // Search in examples
  if (progression.metadata.examples?.some(example => 
    example.toLowerCase().includes(queryLower)
  )) return true;
  
  return false;
}

/**
 * Calculate simple text-based score for a progression
 */
function calculateTextScore(progression: Progression, query: string): number {
  if (!query || !query.trim()) return 10;
  
  const queryLower = query.toLowerCase().trim();
  let score = 10;
  
  // Exact name match
  if (progression.name.toLowerCase() === queryLower) {
    score += 50;
  } else if (progression.name.toLowerCase().includes(queryLower)) {
    score += 30;
  }
  
  // Description match
  if (progression.metadata.description?.toLowerCase().includes(queryLower)) {
    score += 20;
  }
  
  // Pattern match
  if (progression.metadata.pattern?.toLowerCase().includes(queryLower)) {
    score += 25;
  }
  
  // User progression boost
  if (progression.source === 'user') {
    score += 20;
  }
  
  return score;
}

// ================================================================================
// SORTING FUNCTIONS
// ================================================================================

/**
 * Smart sorting function for progressions
 */
export function sortProgressions(progressions: Progression[], sortBy: string, query?: string): Progression[] {
  const sorted = [...progressions];
  
  switch (sortBy) {
    case 'recent':
      return sorted.sort((a, b) => {
        const aTime = Math.max(a.metadata.modified || 0, a.metadata.lastUsed || 0);
        const bTime = Math.max(b.metadata.modified || 0, b.metadata.lastUsed || 0);
        return bTime - aTime;
      });
      
    case 'alphabetical':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
      
    case 'difficulty': {
      const difficultyOrder = { 'beginner': 0, 'intermediate': 1, 'advanced': 2 };
      return sorted.sort((a, b) => {
        const aDiff = difficultyOrder[a.metadata?.difficulty || 'beginner'];
        const bDiff = difficultyOrder[b.metadata?.difficulty || 'beginner'];
        return aDiff - bDiff;
      });
    }
      
    case 'relevance':
    default:
      // Smart relevance sorting
      return sorted.sort((a, b) => {
        // User progressions get priority
        if (a.source === 'user' && b.source !== 'user') return -1;
        if (b.source === 'user' && a.source !== 'user') return 1;
        
        // Recently used gets priority
        const aRecent = Math.max(a.metadata.lastUsed || 0, a.metadata.modified || 0);
        const bRecent = Math.max(b.metadata.lastUsed || 0, b.metadata.modified || 0);
        if (aRecent !== bRecent) return bRecent - aRecent;
        
        // If we have a query, exact name matches get priority
        if (query) {
          const queryLower = query.toLowerCase();
          const aExactMatch = a.name.toLowerCase().includes(queryLower);
          const bExactMatch = b.name.toLowerCase().includes(queryLower);
          if (aExactMatch && !bExactMatch) return -1;
          if (bExactMatch && !aExactMatch) return 1;
        }
        
        // Fall back to alphabetical
        return a.name.localeCompare(b.name);
      });
  }
}

// ================================================================================
// PUBLIC SEARCH API FUNCTIONS
// ================================================================================

/**
 * Search progressions with hybrid filtering and relevance scoring
 * 
 * Primary search function that implements the dual-application approach,
 * combining filter detection with full-text search for optimal results.
 * Supports both simple queries and advanced filtering.
 * 
 * @param {SearchOptions} [options={}] - Search configuration options
 * @returns {Promise<ApiResponse<ScoredProgression[]>>} Search results with scores
 * 
 * @example
 * ```typescript
 * // Simple text search
 * const results = await searchProgressions({
 *   query: "jazz"
 * });
 * 
 * // Natural language search with automatic filter detection
 * const results = await searchProgressions({
 *   query: "easy jazz guitar",
 *   searchMode: "hybrid", // default
 *   sortBy: "relevance",
 *   limit: 20
 * });
 * 
 * // Manual filter-based search
 * const results = await searchProgressions({
 *   filters: {
 *     genres: ["jazz", "blues"],
 *     difficulties: ["beginner"]
 *   },
 *   sortBy: "recent"
 * });
 * ```
 */
export async function searchProgressions(options: SearchOptions = {}): Promise<ApiResponse<ScoredProgression[]>> {
  try {
    // Get all progressions as the base dataset
    const allProgressions = await getAllProgressions();
    if (!allProgressions.success || !allProgressions.data) {
      return createApiResponse(false, undefined, 'Failed to load progressions for search');
    }
    
    const query = options.query || '';
    const searchMode = options.searchMode || 'hybrid';
    

    
    let results: SearchMatch[] = [];
    
    // Choose search strategy based on mode
    switch (searchMode) {
      case 'hybrid':
        // Modern dual application approach - combines filters and text search
        results = hybridSearchProgressions(allProgressions.data, query, options.filters);
        break;
        
      case 'filters_only':
        // Pure filter-based search (no text matching)
        results = allProgressions.data
          .filter(progression => matchesFilters(progression, options.filters || {}))
          .map(progression => ({
            progression,
            score: progression.source === 'user' ? 30 : 10,
            matchReasons: ['🔍 filter match']
          }));
        break;
        
      case 'text_only':
        // Pure text search (no filter detection)
        results = allProgressions.data
          .filter(progression => matchesTextQuery(progression, query))
          .map(progression => ({
            progression,
            score: calculateTextScore(progression, query),
            matchReasons: ['📝 text match']
          }));
        break;
        
      default:
        // Default to hybrid search
        results = hybridSearchProgressions(allProgressions.data, query, options.filters);
    }
    
    // Apply sorting (if not using hybrid relevance)
    if (options.sortBy && options.sortBy !== 'relevance') {
      const progressions = results.map(r => r.progression);
      const sorted = sortProgressions(progressions, options.sortBy, query);
      results = sorted.map((progression: Progression) => ({
        progression,
        score: results.find(r => r.progression.id === progression.id)?.score || 0,
        matchReasons: results.find(r => r.progression.id === progression.id)?.matchReasons || []
      }));
    }
    
    // Apply limit if specified
    if (options.limit && options.limit > 0) {
      results = results.slice(0, options.limit);
    }
    
    // Convert to ScoredProgression format for the response
    const scoredProgressions: ScoredProgression[] = results.map(result => ({
      ...result.progression,
      _score: Math.round(result.score),
      _matches: {
        filterMatches: result.matchReasons.filter(r => r.includes('🎵') || r.includes('📊') || r.includes('📁')),
        textMatches: result.matchReasons.filter(r => r.includes('📝') || r.includes('🎯') || r.includes('🔗'))
      }
    }));
    
    return createApiResponse(true, scoredProgressions);
    
  } catch (error) {
    return createApiResponse(false, undefined, `Enhanced search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}



/**
 * Enhanced text matching with prefix support for metadata fields
 * 
 * Provides comprehensive matching including:
 * - Exact matches (highest score)
 * - Prefix matches for metadata (genre, difficulty, etc.)
 * - Contains matches (current behavior)
 * - Fuzzy matching for common abbreviations
 */
function calculateEnhancedTextMatches(progression: Progression, query: string): {
  score: number;
  matchReasons: string[];
  hasRelevantMatch: boolean;
} {
  const queryLower = query.toLowerCase().trim();
  let score = 0;
  const matchReasons: string[] = [];
  let hasRelevantMatch = false;

  if (!queryLower) return { score: 0, matchReasons: [], hasRelevantMatch: false };

  // =================================================================
  // METADATA PREFIX MATCHING (High Value - 35-50 points)
  // Genre, difficulty, source prefix matching for better discoverability
  // =================================================================

  // Genre prefix matching
  const genre = progression.metadata?.genre?.toLowerCase();
  if (genre) {
    if (genre === queryLower) {
      score += 50;
      matchReasons.push('🎵 exact genre match');
      hasRelevantMatch = true;
    } else if (genre.startsWith(queryLower) && queryLower.length >= 2) {
      score += 35;
      matchReasons.push(`🎵 genre prefix: ${genre}`);
      hasRelevantMatch = true;
    } else if (genre.includes(queryLower) && queryLower.length >= 3) {
      score += 25;
      matchReasons.push(`🎵 genre contains: ${genre}`);
      hasRelevantMatch = true;
    }
  }

  // Difficulty prefix matching
  const difficulty = progression.metadata?.difficulty?.toLowerCase();
  if (difficulty) {
    if (difficulty === queryLower) {
      score += 45;
      matchReasons.push('📊 exact difficulty match');
      hasRelevantMatch = true;
    } else if (difficulty.startsWith(queryLower) && queryLower.length >= 2) {
      score += 30;
      matchReasons.push(`📊 difficulty prefix: ${difficulty}`);
      hasRelevantMatch = true;
    }
  }

  // Source prefix matching
  const source = progression.source?.toLowerCase();
  if (source) {
    if (source === queryLower) {
      score += 40;
      matchReasons.push('📁 exact source match');
      hasRelevantMatch = true;
    } else if (source.startsWith(queryLower) && queryLower.length >= 2) {
      score += 25;
      matchReasons.push(`📁 source prefix: ${source}`);
      hasRelevantMatch = true;
    }
  }

  // Key signature prefix matching (C → C Major, Am → A Minor)
  const key = progression.metadata?.key?.toLowerCase();
  if (key) {
    if (key === queryLower) {
      score += 35;
      matchReasons.push('🎹 exact key match');
      hasRelevantMatch = true;
    } else if (key.startsWith(queryLower) && queryLower.length >= 1) {
      score += 20;
      matchReasons.push(`🎹 key prefix: ${key}`);
      hasRelevantMatch = true;
    } else if (key.includes(queryLower)) {
      score += 15;
      matchReasons.push(`🎹 key contains: ${key}`);
      hasRelevantMatch = true;
    }
  }

  // =================================================================
  // ENHANCED NAME MATCHING (High Value - 20-45 points)
  // Exact > Prefix > Contains hierarchy
  // =================================================================

  const name = progression.name.toLowerCase();
  if (name === queryLower) {
    score += 45;
    matchReasons.push('🎯 exact name match');
    hasRelevantMatch = true;
  } else if (name.startsWith(queryLower) && queryLower.length >= 2) {
    score += 35;
    matchReasons.push('📝 name prefix match');
    hasRelevantMatch = true;
  } else if (name.includes(queryLower)) {
    score += 25;
    matchReasons.push('📝 name contains query');
    hasRelevantMatch = true;
  }

  // =================================================================
  // FIELD-SPECIFIC MATCHING (Medium Value - 15-30 points)
  // Description, pattern, examples with prefix support
  // =================================================================

  // Description matching
  const description = progression.metadata.description?.toLowerCase();
  if (description) {
    if (description.includes(queryLower)) {
      score += 20;
      matchReasons.push('📄 description match');
      hasRelevantMatch = true;
    }
  }

  // Pattern matching
  const pattern = progression.metadata.pattern?.toLowerCase();
  if (pattern) {
    if (pattern === queryLower) {
      score += 30;
      matchReasons.push('🎼 exact pattern match');
      hasRelevantMatch = true;
    } else if (pattern.startsWith(queryLower) && queryLower.length >= 2) {
      score += 25;
      matchReasons.push('🎼 pattern prefix match');
      hasRelevantMatch = true;
    } else if (pattern.includes(queryLower)) {
      score += 20;
      matchReasons.push('🎼 pattern contains query');
      hasRelevantMatch = true;
    }
  }

  // Examples prefix matching
  const examples = progression.metadata.examples || [];
  const exampleMatches = examples.filter(example => {
    const exampleLower = example.toLowerCase();
    return exampleLower === queryLower || 
           exampleLower.startsWith(queryLower) || 
           exampleLower.includes(queryLower);
  });
  if (exampleMatches.length > 0) {
    score += 18;
    matchReasons.push(`💡 example match: ${exampleMatches[0]}`);
    hasRelevantMatch = true;
  }

  // =================================================================
  // CHORD SYMBOL MATCHING (Medium Value - 15-25 points)
  // Exact chord matches and chord prefix matching
  // =================================================================

  const chords = getProgressionChords(progression);
  const chordMatches = chords.filter(chord => {
    const chordLower = chord.toLowerCase();
    return chordLower === queryLower || 
           (chordLower.startsWith(queryLower) && queryLower.length >= 2) ||
           chordLower.includes(queryLower);
  });
  if (chordMatches.length > 0) {
    score += 20;
    matchReasons.push(`🎵 chord match: ${chordMatches.join(', ')}`);
    hasRelevantMatch = true;
  }

  // =================================================================
  // COMMON ABBREVIATION MATCHING (Low-Medium Value - 10-20 points)
  // Handle common music abbreviations and shortcuts
  // =================================================================

  const abbreviations = {
    'beg': 'beginner',
    'int': 'intermediate', 
    'adv': 'advanced',
    'std': 'standard',
    'maj': 'major',
    'min': 'minor',
    'dim': 'diminished',
    'aug': 'augmented'
  };

  const expandedQuery = abbreviations[queryLower as keyof typeof abbreviations];
  if (expandedQuery) {
    // Re-check matches with expanded query
    if (genre === expandedQuery) {
      score += 25;
      matchReasons.push(`🔤 abbreviation: ${queryLower} → ${expandedQuery}`);
      hasRelevantMatch = true;
    } else if (difficulty === expandedQuery) {
      score += 25;
      matchReasons.push(`🔤 abbreviation: ${queryLower} → ${expandedQuery}`);
      hasRelevantMatch = true;
    } else if (source === expandedQuery) {
      score += 20;
      matchReasons.push(`🔤 abbreviation: ${queryLower} → ${expandedQuery}`);
      hasRelevantMatch = true;
    }
  }

  return { score, matchReasons, hasRelevantMatch };
}
